import { useDocumentVisibility, useInterval } from '@mantine/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { ICrmSave } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmIntegrationPage.hooks';
import { useCrmSave } from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmIntegrationPage.hooks';
import { logUserEvents } from '@feathr/hooks';

import { isFundMissing } from './RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import { SOLICIT_CODE_KEY, UNSUBSCRIBE_ACTION_KEY } from './raisersEdgeConfig';
import type { IRaisersEdgeMappingDraft } from './RaisersEdgeMappingsTab/RaisersEdgeMappingsTab.hooks';

const VALIDATED_ATTRIBUTES = [UNSUBSCRIBE_ACTION_KEY, SOLICIT_CODE_KEY].map(
  (key) => `unsubscribe_sync_preference.${key}`,
);

/**
 * Page-level Save & Sync for the changes the tabs stage on the integration and the mapping draft.
 * Leaving the page discards the staged attributes; the draft is page state and drops with it.
 */
export function useRaisersEdgeSave(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
  mappingDraft: IRaisersEdgeMappingDraft,
): ICrmSave {
  const { t } = useTranslation();
  // Person upsync shares `upsync_activities`, so compare donations alone. A clean attribute has no shadow.
  const upsync = integration?.get('upsync_activities');
  const savedUpsync = integration?.shadowAttributes.upsync_activities ?? upsync;
  const isDonationUpsyncOn = !!upsync?.donations;
  const isDonationUpsyncChanged = isDonationUpsyncOn !== !!savedUpsync?.donations;

  return useCrmSave(integration, mappingDraft, {
    validatedAttributes: VALIDATED_ATTRIBUTES,
    getSaveDisabledReason: (model) => {
      // Only a save that touches donation upsync needs a fund, so a bad saved state can't block others.
      const touchesDonations =
        isDonationUpsyncChanged || model.changedStagedAttributes.includes('fundraising');
      return touchesDonations &&
        isFundMissing({
          fundraising: model.get('fundraising'),
          upsync_activities: model.get('upsync_activities'),
        })
        ? t('Select a fund to sync donations to Blackbaud.')
        : undefined;
    },
    // Same event as v1, sent once the PATCH that turns donation upsync on has saved.
    onPatched: (model) => {
      if (isDonationUpsyncChanged && isDonationUpsyncOn) {
        logUserEvents({ integrated_renxt_fundraising: { account_id: model.get('account') } });
      }
    },
  });
}

export const POLL_INTERVAL_MS = 60_000;

/**
 * Counts polls of the page's sync data: one per interval while `enabled` and the tab is visible,
 * and one when the tab comes back into view. Starts at 0; the data's owners do the first load.
 */
export function useRaisersEdgePollCount(enabled: boolean, intervalMs = POLL_INTERVAL_MS): number {
  const [pollCount, setPollCount] = useState(0);
  const isVisible = useDocumentVisibility() === 'visible';
  const shouldPoll = enabled && isVisible;
  // Stable, because `useInterval` restarts the interval each time its callback changes.
  const countPoll = useCallback(() => setPollCount((count) => count + 1), []);
  const interval = useInterval(countPoll, intervalMs);
  const wasHidden = useRef(false);

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }
    interval.start();
    return interval.stop;
  }, [shouldPoll]);

  // Catch up on what changed while the tab was hidden.
  useEffect(() => {
    if (isVisible && wasHidden.current && enabled) {
      countPoll();
    }
    wasHidden.current = !isVisible;
  }, [isVisible]);

  return pollCount;
}
